import TimeRange from "@/types/time-range";
import { PublishedDashboard, WmsMapLayer as WmsLayerConfig } from "@/lib/api";
import { DimensionValueSet } from "@/lib/dimensions";
import {
  dynamicDimensionsContainValues,
  getRelevantDimensionValues,
} from "@/lib/dimensions/helpers";
import BaseWmsLayer from "@/features/shared/widgets/map/layers/base-wms-layer";
import { mapLayerOpacityOverrideToFraction } from "@/features/shared/widgets/map/layer-opacity";
import LayerLegendImageLoader from "@/features/shared/widgets/map/legends/legend-image-loader";
import LegendImage from "@/features/shared/widgets/map/legends/legend-image";
import LegendDimensions from "@/features/shared/widgets/map/legends/legend-dimensions";

import WmsLayerDataLoader, { WmsLayerData } from "./wms-layer-data-loader";

export default class WmsLayer extends BaseWmsLayer {
  private layerDataLoader: WmsLayerDataLoader;
  private legendImageLoader: LayerLegendImageLoader;

  private config;
  private data?: WmsLayerData;

  constructor(config: WmsLayerConfig, selectedDashboard: PublishedDashboard) {
    super(selectedDashboard.id, config.id);
    this.layerDataLoader = new WmsLayerDataLoader(selectedDashboard.id, config.id);
    this.legendImageLoader = new LayerLegendImageLoader(selectedDashboard.id, config.id);
    this.config = config;
  }

  get id() {
    return this.config.id;
  }
  getTitle() {
    return this.config.title;
  }

  get opacity() {
    return mapLayerOpacityOverrideToFraction(this.config);
  }

  get settings() {
    return [];
  }

  get tileSize() {
    return 1024;
  }
  get minZoom() {
    return this.config.min_allowed_zoom_level_override ?? 4;
  }
  get maxZoom() {
    return this.config.max_allowed_zoom_level_override ?? 4;
  }

  get extent() {
    return this.data?.extent;
  }
  get dimensions() {
    return this.data?.dimensions;
  }

  async initialize(timeRange: TimeRange) {
    this.clearLoadFailures();
    try {
      this.data = await this.layerDataLoader.loadLayerData(timeRange);
    } catch (error) {
      this.data = undefined;
      throw error;
    }
  }

  getRelevantDimensionValues(values: DimensionValueSet) {
    return getRelevantDimensionValues(this.dimensions, values);
  }

  isActive(dimensionValues: DimensionValueSet) {
    if (!this.data) return false;
    if (this.hasLoadError(dimensionValues)) return false;
    return dynamicDimensionsContainValues(this.data.dimensions, dimensionValues);
  }

  isForecast() {
    return false;
  }

  async loadLegendImage(dimensionValues: DimensionValueSet) {
    return this.legendImageLoader.loadLegendImage(
      this.getRelevantDimensionValues(dimensionValues),
    );
  }

  renderLegend(dimensionValues: DimensionValueSet) {
    if (!this.config.show_legend) return undefined;

    const currentDimensionValues = this.getRelevantDimensionValues(dimensionValues);
    return (
      <>
        <LegendDimensions dimensionValues={currentDimensionValues} />
        <LegendImage
          layerId={this.id}
          opacity={this.opacity}
          loadLegendImage={this.loadLegendImage.bind(this)}
          dimensionValues={currentDimensionValues}
        />
      </>
    );
  }
}
